import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    browserLocalPersistence, getRedirectResult, GoogleAuthProvider, isSignInWithEmailLink,
    onAuthStateChanged, sendSignInLinkToEmail, setPersistence, signInWithEmailAndPassword,
    signInWithEmailLink, signInWithPopup, signInWithRedirect, User,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../Lib/Firebase";
import { ensureEnrollmentProfile } from "../Lib/enrollmentApi";
import { authPasswordPatchSchema, validated } from "../schemas/firestore";
import PartnerAccessPanel from "./studentAuth/PartnerAccessPanel";
import StudentAccessPanel from "./studentAuth/StudentAccessPanel";
import { friendlyAuthError } from "./studentAuth/types";
import { trackFunnelEvent } from "../Lib/funnelAnalytics";
import "../themes/student-theme.css";

export type AuthPortal = "student" | "partner";
const PORTAL_KEY = "mockzo_auth_portal";
const FLOW_KEY = "mockzo_auth_in_progress";
const ACTIVATION_KEY = "mockzo_activation_token";
const RETURN_TO_KEY = "mockzo_student_return_to";
const EMAIL_KEY = "mockzo_email_for_signin";
const OPEN_REQUEST_KEY = "mockzo_auth_open_requested";
const OPEN_REQUEST_EVENT = "mockzo:open-auth";
let openModalCallback: ((portal: AuthPortal) => void) | null = null;
let redirectResult: ReturnType<typeof getRedirectResult> | null = null;

const safePath = (value?: string | null) => value && value.startsWith("/") && !value.startsWith("//") ? value : "";
const consumeRedirect = () => redirectResult || (redirectResult = getRedirectResult(auth));

export function openAuthModal(portal: AuthPortal = "student", returnTo?: string) {
    window.localStorage.setItem(PORTAL_KEY, portal);
    const destination = safePath(returnTo);
    if (portal === "student" && destination) window.localStorage.setItem(RETURN_TO_KEY, destination);
    window.localStorage.setItem(OPEN_REQUEST_KEY, "true");
    if (openModalCallback) {
        openModalCallback(portal);
        return;
    }
    window.dispatchEvent(new CustomEvent(OPEN_REQUEST_EVENT));
}

export function isStudentAuthFlowActive() {
    return window.localStorage.getItem(FLOW_KEY) === "true" && window.localStorage.getItem(PORTAL_KEY) !== "partner";
}

function nextDestination(portal: AuthPortal) {
    if (window.localStorage.getItem(ACTIVATION_KEY)) return "/activate";
    if (portal === "partner") return "/partners";
    const requested = safePath(window.localStorage.getItem(RETURN_TO_KEY));
    if (requested) {
        window.localStorage.removeItem(RETURN_TO_KEY);
        return requested;
    }
    const tab = window.localStorage.getItem("mockzo_student_return_tab");
    if (tab === "resources") return "/dashboard?tab=explore&section=resources";
    if (tab === "courses" || tab === "programs") return "/dashboard?tab=programs";
    return "/dashboard";
}

export default function AuthModal() {
    const [open, setOpen] = useState(false);
    const [portal, setPortal] = useState<AuthPortal>("student");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [sent, setSent] = useState(false);
    const [emailLinkPending, setEmailLinkPending] = useState(false);
    const [googleBusy, setGoogleBusy] = useState(false);
    const [emailBusy, setEmailBusy] = useState(false);
    const [passwordBusy, setPasswordBusy] = useState(false);
    const routing = useRef(false);
    const dialog = useRef<HTMLDivElement>(null);

    const begin = (nextPortal = portal) => {
        window.localStorage.setItem(PORTAL_KEY, nextPortal);
        window.localStorage.setItem(FLOW_KEY, "true");
        if (nextPortal === "student") trackFunnelEvent("auth_start", { hasReturnPath: Boolean(window.localStorage.getItem(RETURN_TO_KEY)) });
    };
    const navigate = useCallback((nextPortal: AuthPortal) => {
        window.localStorage.removeItem(FLOW_KEY);
        window.location.replace(nextDestination(nextPortal));
    }, []);
    const finishAuthentication = useCallback(async (user: User) => {
        if (routing.current) return;
        routing.current = true;
        const nextPortal = window.localStorage.getItem(PORTAL_KEY) === "partner" ? "partner" : "student";
        if (nextPortal === "partner" || window.localStorage.getItem(ACTIVATION_KEY)) {
            navigate(nextPortal);
            return;
        }
        void ensureEnrollmentProfile().catch(() => undefined);
        try {
            const reference = doc(db, "users", user.uid);
            const snapshot = await getDoc(reference);
            const current = snapshot.data() || {};
            const parts = String(current.name || user.displayName || "").trim().split(/\s+/).filter(Boolean);
            await setDoc(reference, {
                uid: user.uid, email: current.email || user.email || "", name: current.name || user.displayName || "Student",
                firstName: current.firstName || parts[0] || "", lastName: current.lastName || parts.slice(1).join(" "),
                photoURL: current.photoURL || user.photoURL || "", createdAt: current.createdAt || serverTimestamp(), updatedAt: serverTimestamp(),
            }, { merge: true });
        } catch {
            // Profile creation is best-effort; it must never block a signed-in student from continuing.
        }
        trackFunnelEvent("auth_complete", { method: user.providerData[0]?.providerId || "email", hasReturnPath: Boolean(window.localStorage.getItem(RETURN_TO_KEY)) });
        navigate("student");
    }, [navigate]);
    const close = useCallback(() => {
        window.localStorage.removeItem(FLOW_KEY);
        if (auth.currentUser) { navigate(portal); return; }
        setOpen(false);
    }, [navigate, portal]);

    useEffect(() => {
        const show = (nextPortal: AuthPortal) => {
            routing.current = false;
            setPortal(nextPortal);
            setEmail(""); setPassword(""); setError(""); setSent(false); setEmailLinkPending(false);
            setGoogleBusy(false); setEmailBusy(false); setPasswordBusy(false); setOpen(true);
            window.localStorage.removeItem(OPEN_REQUEST_KEY);
        };
        const showRequested = () => {
            if (window.localStorage.getItem(OPEN_REQUEST_KEY) !== "true") return;
            show(window.localStorage.getItem(PORTAL_KEY) === "partner" ? "partner" : "student");
        };
        openModalCallback = show;
        window.addEventListener(OPEN_REQUEST_EVENT, showRequested);
        showRequested();
        return () => { openModalCallback = null; window.removeEventListener(OPEN_REQUEST_EVENT, showRequested); };
    }, []);
    useEffect(() => {
        const stop = onAuthStateChanged(auth, (user) => {
            if (user && window.localStorage.getItem(FLOW_KEY) === "true") void finishAuthentication(user);
        });
        consumeRedirect().then((result) => result?.user ? finishAuthentication(result.user) : undefined).catch((nextError) => {
            window.localStorage.removeItem(FLOW_KEY); setError(friendlyAuthError(nextError, "Google sign-in could not be completed.")); setOpen(true);
        });
        return stop;
    }, [finishAuthentication]);
    useEffect(() => {
        if (window.location.pathname.startsWith("/checkout/course/")) return;
        if (!isSignInWithEmailLink(auth, window.location.href)) return;
        const linkPortal = window.localStorage.getItem(PORTAL_KEY) === "partner" ? "partner" : "student";
        setPortal(linkPortal); setOpen(true);
        window.localStorage.setItem(PORTAL_KEY, linkPortal); window.localStorage.setItem(FLOW_KEY, "true");
        const stored = window.localStorage.getItem(EMAIL_KEY) || "";
        if (!stored) { setEmailLinkPending(true); return; }
        setEmail(stored); setEmailBusy(true);
        signInWithEmailLink(auth, stored, window.location.href).then((credential) => finishAuthentication(credential.user)).catch((nextError) => {
            routing.current = false; setError(friendlyAuthError(nextError, "This sign-in link is invalid or expired."));
        }).finally(() => setEmailBusy(false));
    }, [finishAuthentication]);
    useEffect(() => {
        if (!open) return;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") close();
            if (event.key !== "Tab") return;
            const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled), a[href]") || []);
            if (!controls.length) return;
            const first = controls[0]; const last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        document.addEventListener("keydown", handleKey);
        return () => { document.body.style.overflow = overflow; document.removeEventListener("keydown", handleKey); };
    }, [open, close]);

    const sendOrCompleteEmail = async () => {
        setError("");
        const normalized = email.trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) { setError("Enter a valid email address."); return; }
        setEmailBusy(true); begin();
        try {
            if (emailLinkPending) {
                const credential = await signInWithEmailLink(auth, normalized, window.location.href);
                window.localStorage.removeItem(EMAIL_KEY); setEmailLinkPending(false); await finishAuthentication(credential.user);
            } else {
                await sendSignInLinkToEmail(auth, normalized, { url: `${window.location.origin}/`, handleCodeInApp: true });
                window.localStorage.setItem(EMAIL_KEY, normalized); setEmail(normalized); setSent(true);
            }
        } catch (nextError) {
            routing.current = false; setError(friendlyAuthError(nextError, emailLinkPending ? "This sign-in link could not be completed." : "We could not send the sign-in link."));
        } finally { setEmailBusy(false); }
    };
    const passwordSignIn = async () => {
        setError("");
        if (!email.trim() || !password) { setError("Enter your email address and password."); return; }
        setPasswordBusy(true); begin();
        try {
            await setPersistence(auth, browserLocalPersistence);
            const credential = await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
            await setDoc(doc(db, "users", credential.user.uid), validated(authPasswordPatchSchema, {
                mustResetPassword: false, passwordSetAt: serverTimestamp(),
            }), { merge: true }).catch(() => undefined);
            await finishAuthentication(credential.user);
        } catch (nextError) {
            routing.current = false; window.localStorage.removeItem(FLOW_KEY);
            setError(friendlyAuthError(nextError, "Password sign-in failed. Please try again."));
        } finally { setPasswordBusy(false); }
    };
    const googleSignIn = async () => {
        setError(""); setGoogleBusy(true); begin();
        const provider = new GoogleAuthProvider(); provider.addScope("email"); provider.addScope("profile");
        try {
            await setPersistence(auth, browserLocalPersistence);
            if (!(window as any).__codexWebMcpModelContext) {
                const credential = await signInWithPopup(auth, provider); await finishAuthentication(credential.user); return;
            }
            await signInWithRedirect(auth, provider);
        } catch (nextError: any) {
            routing.current = false; window.localStorage.removeItem(FLOW_KEY);
            setError(friendlyAuthError(nextError, "Google sign-in could not be started.")); setGoogleBusy(false);
        }
    };
    if (!open) return null;
    const accessProps = { email, password, error, sent, googleBusy, emailBusy, passwordBusy, onEmail: setEmail, onPassword: setPassword, onGoogle: googleSignIn, onEmailLink: sendOrCompleteEmail, onPasswordSignIn: passwordSignIn, onClose: close };
    return createPortal(<div id="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-modal-title" className="mz-auth-theme fixed inset-0 z-[12000] flex items-center justify-center overflow-y-auto p-2 sm:p-5">
        <button type="button" aria-label="Close sign in" onClick={close} className="absolute inset-0 cursor-default bg-slate-950/70 backdrop-blur-md" />
        <div ref={dialog} className={`relative z-10 max-h-[calc(100dvh-1rem)] w-full overflow-y-auto rounded-[1.75rem] bg-white text-slate-900 shadow-2xl ring-1 ring-white/20 ${portal === "student" ? "max-w-5xl" : "max-w-3xl"}`}>
            {portal === "student" ? <StudentAccessPanel email={email} error={error} sent={sent} googleBusy={googleBusy} emailBusy={emailBusy} completingLink={emailLinkPending} onEmail={(value) => { setEmail(value); setSent(false); }} onGoogle={googleSignIn} onEmailAction={sendOrCompleteEmail} onClose={close} />
                : <PartnerAccessPanel {...accessProps} />}
        </div>
    </div>, document.body);
}
